import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { UserPlus } from "@phosphor-icons/react";

const DEPARTMENTS = ["Executive","Finance","Legal","Investigations","Consulting","HR","Quality","Operations","Field"];

export default function InviteUser() {
  const { user } = useAuth();
  const can = (user?.ux_tier || 0) >= 4;
  const [form, setForm] = useState({ email: "", name: "", department: "Investigations", ux_tier: 2, designation: "Team Member", role: "Investigator" });
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setErr(""); setResult(null); setBusy(true);
    try {
      const { data } = await api.post("/admin/users/invite", form);
      setResult(data);
      setForm({ email: "", name: "", department: "Investigations", ux_tier: 2, designation: "Team Member", role: "Investigator" });
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || "Invite failed."); }
    finally { setBusy(false); }
  };

  if (!can) return <div className="p-6 text-[var(--text-secondary)]">Invite-user requires Tier 4+ (COO/CEO).</div>;

  return (
    <div data-testid="invite-user-page" className="p-6 max-w-2xl space-y-5">
      <h1 className="text-2xl font-bold text-[#263776] flex items-center gap-2">
        <UserPlus size={28} className="text-[#F47D20]" />
        Invite Team Member
      </h1>
      <div className="bg-white p-5 border border-[var(--border)] rounded space-y-3">
        <Field label="Email" testid="invite-email" value={form.email} onChange={v => setForm({...form, email: v})} />
        <Field label="Full name" testid="invite-name" value={form.name} onChange={v => setForm({...form, name: v})} />
        <div className="grid grid-cols-2 gap-3">
          <SelectField label="Department" testid="invite-dept" value={form.department} options={DEPARTMENTS} onChange={v => setForm({...form, department: v})} />
          <SelectField label="UX Tier" testid="invite-tier" value={form.ux_tier} options={[1,2,3,4,5]} onChange={v => setForm({...form, ux_tier: parseInt(v)})} />
        </div>
        <Field label="Designation" testid="invite-designation" value={form.designation} onChange={v => setForm({...form, designation: v})} />
        <Field label="Role" testid="invite-role" value={form.role} onChange={v => setForm({...form, role: v})} />
        <button data-testid="invite-submit" onClick={submit} disabled={!form.email || !form.name || busy}
          className="bg-[#F47D20] text-white px-5 py-2 text-sm rounded hover:bg-[#c96009] disabled:opacity-50">
          {busy ? "Sending invite…" : "Send invite"}
        </button>
        {err && <div data-testid="invite-err" className="bg-rose-50 border border-rose-200 text-rose-700 px-3 py-2 rounded text-sm">{err}</div>}
        {result && (
          <div data-testid="invite-result" className="bg-emerald-50 border border-emerald-200 text-emerald-800 px-4 py-3 rounded text-sm space-y-1">
            <div>✓ Invite created for <strong>{result.email}</strong></div>
            <div>Temp password: <code className="bg-white px-2 py-0.5 rounded border">{result.temp_password}</code></div>
            <div>Email sent: <strong>{result.invite_email_sent ? "Yes" : "No (please share temp password manually)"}</strong></div>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, testid, value, onChange }) {
  return (
    <div>
      <label className="block text-xs text-[var(--text-muted)] mb-1">{label}</label>
      <input data-testid={testid} value={value} onChange={e => onChange(e.target.value)}
        className="w-full border border-[var(--border-strong)] rounded px-3 py-2 text-sm" />
    </div>
  );
}
function SelectField({ label, testid, value, options, onChange }) {
  return (
    <div>
      <label className="block text-xs text-[var(--text-muted)] mb-1">{label}</label>
      <select data-testid={testid} value={value} onChange={e => onChange(e.target.value)}
        className="w-full border border-[var(--border-strong)] rounded px-3 py-2 text-sm">
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}
